import api from './api';

/**
 * S3 Upload Service for direct browser uploads
 * This service handles client-side uploads to S3 using presigned URLs
 */

/**
 * Upload a file directly to S3 using presigned URL
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'offer-templates')
 * @param {function} onProgress - Progress callback function
 * @returns {Promise<string>} - Returns the S3 file URL
 */
export const uploadToS3 = async (file, folder = 'offer-templates', onProgress = null) => {
  try {
    console.log('🚀 Starting S3 upload for:', file.name);

    // Step 1: Get presigned URL from backend
    const presignedResponse = await api.post('/offer-templates/s3-presigned-url', {
      fileName: file.name,
      fileType: file.type,
      folder: folder
    });

    const { uploadUrl, fileUrl, key } = presignedResponse.data;
    console.log('✅ Got presigned URL:', { uploadUrl, fileUrl, key });

    // Step 2: Upload file directly to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
    }

    console.log('✅ File uploaded successfully to S3:', fileUrl);
    return fileUrl;

  } catch (error) {
    console.error('❌ S3 upload error:', error);
    
    // Check if S3 is not available and we should use local upload
    if (error.response?.data?.useLocalUpload) {
      console.log('🔄 S3 not available, falling back to local upload');
      throw new Error('S3_NOT_AVAILABLE');
    }
    
    throw error;
  }
};

/**
 * Upload with progress tracking using XMLHttpRequest
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'offer-templates')
 * @param {function} onProgress - Progress callback function (receives percentage)
 * @returns {Promise<string>} - Returns the S3 file URL
 */
export const uploadToS3WithProgress = async (file, folder = 'offer-templates', onProgress = null) => {
  try {
    console.log('🚀 Starting S3 upload with progress for:', file.name);

    // Step 1: Get presigned URL from backend
    const presignedResponse = await api.post('/offer-templates/s3-presigned-url', {
      fileName: file.name,
      fileType: file.type,
      folder: folder
    });

    const { uploadUrl, fileUrl, key } = presignedResponse.data;
    console.log('✅ Got presigned URL:', { uploadUrl, fileUrl, key });

    // Step 2: Upload file directly to S3 with progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          console.log('✅ File uploaded successfully to S3 with progress:', fileUrl);
          resolve(fileUrl);
        } else {
          reject(new Error(`S3 upload failed: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('S3 upload failed: Network error'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });

  } catch (error) {
    console.error('❌ S3 upload with progress error:', error);
    
    // Check if S3 is not available and we should use local upload
    if (error.response?.data?.useLocalUpload) {
      console.log('🔄 S3 not available, falling back to local upload');
      throw new Error('S3_NOT_AVAILABLE');
    }
    
    throw error;
  }
};

/**
 * Fallback to local upload when S3 is not available
 * @param {File} file - The file to upload
 * @returns {Promise<string>} - Returns the local file URL
 */
export const uploadToLocal = async (file) => {
  try {
    console.log('🔄 Using local upload for:', file.name);
    
    const formData = new FormData();
    formData.append('files', file);
    
    const response = await api.post('/offer-templates/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    const url = response.data.attachments?.[0]?.fileUrl;
    if (!url) {
      throw new Error('No file URL returned from local upload');
    }
    
    console.log('✅ File uploaded to local storage:', url);
    return url;
    
  } catch (error) {
    console.error('❌ Local upload error:', error);
    throw error;
  }
};

/**
 * Universal upload function that tries S3 first, falls back to local
 * @param {File} file - The file to upload
 * @param {string} folder - S3 folder (default: 'offer-templates')
 * @param {function} onProgress - Progress callback function
 * @returns {Promise<{url: string, method: string}>} - Returns file URL and upload method used
 */
export const uploadFile = async (file, folder = 'offer-templates', onProgress = null) => {
  try {
    // Try S3 upload first
    const url = await uploadToS3WithProgress(file, folder, onProgress);
    return { url, method: 's3' };
  } catch (error) {
    if (error.message === 'S3_NOT_AVAILABLE') {
      // Fall back to local upload
      const url = await uploadToLocal(file);
      return { url, method: 'local' };
    }
    throw error;
  }
};

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {boolean} - Returns true if valid, throws error if invalid
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = Infinity, // No size limit
    allowedTypes = ['image/', 'video/']
  } = options;

  if (!file) {
    throw new Error('No file provided');
  }

  if (file.size > maxSize) {
    throw new Error(`File size exceeds ${Math.round(maxSize / 1024 / 1024)}MB limit`);
  }

  const isValidType = allowedTypes.some(type => file.type.startsWith(type));
  if (!isValidType) {
    throw new Error(`File type ${file.type} is not allowed`);
  }

  return true;
};

export default {
  uploadToS3,
  uploadToS3WithProgress,
  uploadToLocal,
  uploadFile,
  validateFile
};
